/**
 * WhatsApp message costs and allocated marketing costs of one Echo (EMC,
 * plans/2026-10-21-echo-whatsapp-marketing-costs-ledger-plan.md §4.6;
 * contracts plans/evidence/2026-10-21-emc/contracts.md C3.4, C3.7, C4.3, C7).
 *
 * Pure functions over the ledger rows (`GET /video-sessions/{id}/costs`) and
 * the session summary, so the cards stay thin and every figure is unit-tested
 * without a network. Amounts on the rows are USD (the ledger currency); the
 * native EUR amount and the ECB rate live in `usage_detail`.
 */

import {
  MARKETING_COST_DOMAIN,
  WHATSAPP_COST_DOMAIN,
  costClassOf,
} from "../costs/costClass";
import type {
  EchoflicksSessionCostLineItem,
  EchoflicksVideoSession,
} from "../types";

// ---------------------------------------------------------------------------
// Shared number helpers
// ---------------------------------------------------------------------------

export function finite(value: unknown): number | null {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function rowUsd(cost: EchoflicksSessionCostLineItem): number | null {
  return finite(
    cost.effective_cost_usd ??
      cost.provider_reported_cost_usd ??
      cost.estimated_cost_usd,
  );
}

function humanize(value: string): string {
  return value.replaceAll("_", " ").replaceAll("-", " ");
}

function capitalize(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

const LOWER_WORDS = new Set(["of", "and"]);

/** "rest_of_western_europe" -> "Rest of Western Europe". */
function titleCase(value: string): string {
  return humanize(value)
    .split(" ")
    .filter(Boolean)
    .map((word, index) =>
      index > 0 && LOWER_WORDS.has(word) ? word : capitalize(word),
    )
    .join(" ");
}

/** "€0.0166": per-message prices need four decimals, totals two. */
export function formatEur(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "Unknown";
  if (value === 0) return "€0";
  const small = Math.abs(value) < 1;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: small ? 4 : 2,
  }).format(value);
}

/** "€389.95": an amount in EUR minor units, always two decimals. */
export function formatEurMinor(minor: number | null | undefined): string {
  if (minor == null || !Number.isFinite(minor)) return "Unknown";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

/** "1.0850 USD per EUR (ECB 2026-10-20)", or null without a rate. */
export function formatFx(
  detail?: Record<string, string> | null,
): string | null {
  const rate = finite(detail?.fx_rate_usd_per_eur);
  if (rate == null) return null;
  const date = detail?.fx_date?.trim();
  const source = detail?.fx_source?.trim().toLowerCase();
  const who = source && source !== "ecb" ? source.toUpperCase() : "ECB";
  return `${rate.toFixed(4)} USD per EUR (${who}${date ? ` ${date}` : ""})`;
}

// ---------------------------------------------------------------------------
// Purposes (C7) and Meta's vocabulary
// ---------------------------------------------------------------------------

export type PurposeGroup =
  | "interview"
  | "delivery"
  | "aftercare"
  | "onboarding"
  | "support"
  | "edits"
  | "other";

export const PURPOSE_GROUPS: { key: PurposeGroup; label: string }[] = [
  { key: "interview", label: "Interview" },
  { key: "delivery", label: "Delivery" },
  { key: "aftercare", label: "Aftercare" },
  { key: "onboarding", label: "Onboarding (claimed before this Echo)" },
  { key: "support", label: "Support" },
  { key: "edits", label: "Edits" },
  { key: "other", label: "Other" },
];

const PURPOSE_GROUP_LABELS = Object.fromEntries(
  PURPOSE_GROUPS.map((group) => [group.key, group.label]),
) as Record<PurposeGroup, string>;

/**
 * Every C7 purpose with its group and a short label. Purposes of the
 * `marketing_message` and `account` classes never land on an Echo by design
 * (they are marketing pools or contact overhead); they read as Other if a
 * row ever carries one.
 */
const PURPOSES: Record<string, { group: PurposeGroup; label: string }> = {
  interview_prompt: { group: "interview", label: "Interview question" },
  interview_menu: { group: "interview", label: "Interview menu" },
  echo_confirm: { group: "interview", label: "Confirm the Echo" },
  echo_upload_reminder: { group: "interview", label: "Upload reminder" },
  echo_inactivity_nudge: { group: "interview", label: "Inactivity nudge" },
  echo_studio: { group: "interview", label: "Studio flow" },
  echo_progress: { group: "delivery", label: "Progress update" },
  echo_delivery: { group: "delivery", label: "Film delivery" },
  echo_poster: { group: "delivery", label: "Poster" },
  echo_redelivery: { group: "delivery", label: "Redelivery" },
  echo_error_notice: { group: "delivery", label: "Error notice" },
  echo_rating: { group: "aftercare", label: "Rating ask" },
  echo_gallery_cta: { group: "aftercare", label: "Gallery link" },
  echo_share: { group: "aftercare", label: "Share link" },
  echo_hd_email_ask: { group: "aftercare", label: "HD email ask" },
  echo_edit: { group: "edits", label: "Edit" },
  onboarding_welcome: { group: "onboarding", label: "Welcome" },
  onboarding_ceo_note: { group: "onboarding", label: "CEO note" },
  onboarding_consent: { group: "onboarding", label: "Consent" },
  onboarding_optin: { group: "onboarding", label: "Opt-in" },
  onboarding_consent_nudge: { group: "onboarding", label: "Consent nudge" },
  felix_reply: { group: "support", label: "Felix reply" },
  operator_reply: { group: "support", label: "Operator reply" },
  support_generic: { group: "support", label: "Support" },
  reengagement_nudge: { group: "other", label: "Re-engagement nudge" },
  repeat_echo_nudge: { group: "other", label: "Repeat Echo nudge" },
  referral_nudge: { group: "other", label: "Referral nudge" },
  exhausted_campaign: { group: "other", label: "Exhausted campaign" },
  cart_recovery: { group: "other", label: "Cart recovery" },
  broadcast: { group: "other", label: "Broadcast" },
  payment_receipt: { group: "other", label: "Payment receipt" },
  referral_reward_notice: { group: "other", label: "Referral reward" },
  auth_otp: { group: "other", label: "Login code" },
  account_notice: { group: "other", label: "Account notice" },
  unclassified: { group: "other", label: "Unclassified" },
};

/** Group of a purpose; without one, the row's attribution class decides. */
export function purposeGroupOf(
  purpose?: string | null,
  attributionClass?: string | null,
): PurposeGroup {
  const key = purpose?.trim().toLowerCase();
  if (key && PURPOSES[key]) return PURPOSES[key].group;
  switch (attributionClass?.trim().toLowerCase()) {
    case "onboarding":
      return "onboarding";
    case "support":
      return "support";
    default:
      return "other";
  }
}

export function purposeGroupLabel(group: PurposeGroup): string {
  return PURPOSE_GROUP_LABELS[group];
}

export function purposeLabel(purpose?: string | null): string {
  const key = purpose?.trim().toLowerCase();
  if (!key) return "No purpose";
  return PURPOSES[key]?.label ?? capitalize(humanize(key));
}

const PRICING_TYPE_LABELS: Record<string, string> = {
  regular: "Charged",
  free_customer_service: "Free tier",
  free_entry_point: "Free entry point",
  free_group_customer_service: "Free tier (group)",
};

/** Meta `pricing.type` in plain words ("Free tier", "Free entry point"). */
export function pricingTypeLabel(type?: string | null): string {
  const key = type?.trim().toLowerCase();
  if (!key) return "Not priced yet";
  return PRICING_TYPE_LABELS[key] ?? capitalize(humanize(key));
}

const CATEGORY_LABELS: Record<string, string> = {
  service: "Service",
  utility: "Utility",
  marketing: "Marketing",
  marketing_lite: "Marketing lite",
  authentication: "Authentication",
  authentication_international: "Authentication (international)",
};

/** Meta `pricing.category`, normalized as the indexer does (C2). */
export function categoryLabel(category?: string | null): string {
  const key = category?.trim().toLowerCase().replaceAll("-", "_");
  if (!key) return "Unknown";
  return CATEGORY_LABELS[key] ?? capitalize(humanize(key));
}

const LIFECYCLE_LABELS: Record<string, string> = {
  sent: "Sent",
  delivered: "Delivered",
  read: "Read",
  failed: "Failed",
  undelivered: "Not delivered",
};

export function messageStatusLabel(status?: string | null): string {
  const key = status?.trim().toLowerCase();
  if (!key) return "Unknown";
  return LIFECYCLE_LABELS[key] ?? capitalize(humanize(key));
}

/** "Spain (ES)" from the rate market and the recipient's country. */
export function marketLabel(
  detail?: Record<string, string> | null,
): string | null {
  const market = detail?.market?.trim();
  const country = detail?.recipient_country?.trim().toUpperCase();
  if (market && country) return `${titleCase(market)} (${country})`;
  if (market) return titleCase(market);
  return country || null;
}

// ---------------------------------------------------------------------------
// One WhatsApp row (C3.4)
// ---------------------------------------------------------------------------

export function isWhatsAppCost(cost: EchoflicksSessionCostLineItem): boolean {
  return (cost.cost_domain ?? "").toLowerCase() === WHATSAPP_COST_DOMAIN;
}

export function isMarketingCost(cost: EchoflicksSessionCostLineItem): boolean {
  return (cost.cost_domain ?? "").toLowerCase() === MARKETING_COST_DOMAIN;
}

export type WhatsAppBillingState =
  | "pending"
  | "charged"
  | "free"
  | "undelivered"
  | "failed"
  | "unpriced";

const BILLING_STATES = new Set<WhatsAppBillingState>([
  "pending",
  "charged",
  "free",
  "undelivered",
  "failed",
  "unpriced",
]);

/**
 * The row's C3.2 billing state. The indexer stamps it on every row; an older
 * row without it is read from `billable`, the lifecycle and Meta's type.
 */
export function whatsAppBillingState(
  cost: EchoflicksSessionCostLineItem,
): WhatsAppBillingState {
  const stamped = cost.usage_detail?.billing_state?.trim().toLowerCase();
  if (stamped && BILLING_STATES.has(stamped as WhatsAppBillingState)) {
    return stamped as WhatsAppBillingState;
  }
  const lifecycle = cost.lifecycle_status?.trim().toLowerCase();
  if (lifecycle === "failed") return "failed";
  if (lifecycle === "undelivered") return "undelivered";
  const type = cost.usage_detail?.pricing_type?.trim().toLowerCase();
  if (type?.startsWith("free")) return "free";
  if (cost.billable === true) {
    return rowUsd(cost) == null ? "unpriced" : "charged";
  }
  if (lifecycle === "sent") return "pending";
  return "unpriced";
}

export type UnpricedReason = "no_pricing" | "no_rate" | "fx_unavailable";

export const UNPRICED_REASON_LABELS: Record<UnpricedReason, string> = {
  no_pricing: "Meta sent no price",
  no_rate: "No rate for this market and category",
  fx_unavailable: "No ECB rate for the day",
};

/**
 * Why an unpriced row has no price. C3.2 names the reasons but C3.4 gives
 * them no field of their own, so an explicit `unpriced_reason` (or the ECL
 * `unknown_reason`) wins, else the reason is read from what the row lacks.
 */
export function unpricedReasonOf(
  cost: EchoflicksSessionCostLineItem,
): UnpricedReason | null {
  if (whatsAppBillingState(cost) !== "unpriced") return null;
  const detail = cost.usage_detail ?? {};
  const explicit = (detail.unpriced_reason ?? detail.unknown_reason)
    ?.trim()
    .toLowerCase();
  if (
    explicit === "no_pricing" ||
    explicit === "no_rate" ||
    explicit === "fx_unavailable"
  ) {
    return explicit;
  }
  if (!detail.pricing_type && !detail.pricing_category) return "no_pricing";
  if (finite(detail.price_eur) != null && !detail.fx_rate_usd_per_eur) {
    return "fx_unavailable";
  }
  return "no_rate";
}

/** The row's native EUR amount (`price_eur`, else `eur_micros`). */
export function whatsAppEur(
  cost: EchoflicksSessionCostLineItem,
): number | null {
  const state = whatsAppBillingState(cost);
  // Only a charged (or charged but unpriced) message costs money. A message
  // still waiting for its delivery receipt is not charged yet.
  if (state !== "charged" && state !== "unpriced") return 0;
  const detail = cost.usage_detail;
  const price = finite(detail?.price_eur);
  if (price != null) return price;
  const micros = finite(detail?.eur_micros);
  return micros == null ? null : micros / 1_000_000;
}

/** USD the row adds to the Echo: 0 unless Meta charged it. */
export function whatsAppUsd(
  cost: EchoflicksSessionCostLineItem,
): number | null {
  if (cost.billable === false) return 0;
  const state = whatsAppBillingState(cost);
  if (state !== "charged" && state !== "unpriced") return 0;
  return rowUsd(cost);
}

/** Meta's daily report already covers the row (C3.8 `stamp_whatsapp_cells`). */
export function isReconciledWithMeta(
  cost: Pick<EchoflicksSessionCostLineItem, "confidence" | "usage_detail">,
): boolean {
  return (
    cost.confidence === "priced_from_provider_daily_cell" ||
    Boolean(cost.usage_detail?.cell_reconciled_at?.trim())
  );
}

export const EVIDENCE_MATCHED = "Matches Meta's daily bill";
export const EVIDENCE_WAITING = "Waiting for Meta's daily report";

/** Rows that Meta's daily report should cover: delivered, charged or free. */
export function awaitsMetaReport(cost: EchoflicksSessionCostLineItem): boolean {
  const state = whatsAppBillingState(cost);
  return state === "charged" || state === "free" || state === "unpriced";
}

/** The evidence chip of a row, or null for a row Meta does not bill. */
export function whatsAppEvidenceLabel(
  cost: EchoflicksSessionCostLineItem,
): string | null {
  if (!awaitsMetaReport(cost)) return null;
  return isReconciledWithMeta(cost) ? EVIDENCE_MATCHED : EVIDENCE_WAITING;
}

/** "Film delivery · template echo_ready · Service · Free tier · Spain (ES)". */
export function formatWhatsAppDetail(
  detail?: Record<string, string> | null,
): string | null {
  if (!detail) return null;
  const parts = [
    detail.purpose ? purposeLabel(detail.purpose) : null,
    detail.template_name ? `template ${detail.template_name}` : null,
    detail.pricing_category ? categoryLabel(detail.pricing_category) : null,
    detail.pricing_type ? pricingTypeLabel(detail.pricing_type) : null,
    marketLabel(detail),
    detail.attribution_rule === "claimed_onboarding"
      ? "claimed from onboarding"
      : null,
    detail.split_ratio ? `split ${detail.split_ratio}` : null,
  ].filter((part): part is string => Boolean(part));
  return parts.length ? parts.join(" · ") : null;
}

// ---------------------------------------------------------------------------
// The WhatsApp messages card (the summary itself is in echoMessagingSummary.ts)
// ---------------------------------------------------------------------------

/** Whether the session carries any C3.7 WhatsApp field. */
export function hasWhatsAppSummary(
  session: Pick<
    EchoflicksVideoSession,
    "echo_cost_whatsapp_messages" | "echo_cost_messaging_usd"
  >,
): boolean {
  return (
    finite(session.echo_cost_whatsapp_messages) != null ||
    finite(session.echo_cost_messaging_usd) != null
  );
}

// ---------------------------------------------------------------------------
// Allocated marketing (C4.3)
// ---------------------------------------------------------------------------

const POOL_LABELS: Record<string, string> = {
  meta_ads: "Meta ads",
  whatsapp_marketing: "WhatsApp marketing",
  meta_whatsapp: "WhatsApp marketing",
};

export function poolLabel(pool?: string | null): string {
  const key = pool?.trim().toLowerCase();
  if (!key) return "Marketing";
  return POOL_LABELS[key] ?? capitalize(humanize(key));
}

/** "Meta ads · launchpad · week 2026-W42 · 1/126". */
export function formatMarketingDetail(
  detail?: Record<string, string> | null,
): string | null {
  if (!detail) return null;
  const parts = [
    detail.pool ? poolLabel(detail.pool) : null,
    detail.source ?? null,
    detail.period ? `week ${detail.period}` : null,
    detail.allocated_ratio ?? null,
    detail.allocation_status === "provisional" ? "provisional" : null,
  ].filter((part): part is string => Boolean(part));
  return parts.length ? parts.join(" · ") : null;
}

// ---------------------------------------------------------------------------
// Class totals (EMC-071)
// ---------------------------------------------------------------------------

/** Sum of the billable USD of some rows; null when none has a price. */
export function sumBillableUsd(
  rows: EchoflicksSessionCostLineItem[],
): number | null {
  const values = rows
    .filter((cost) => cost.billable !== false)
    .map(rowUsd)
    .filter((value): value is number => value != null);
  return values.length ? values.reduce((sum, value) => sum + value, 0) : null;
}

/**
 * One class total for the headline: the C3.7 field unless the loaded ledger
 * rows disagree with it by a cent or more (the summary is projected
 * asynchronously; the same rule as the funding strip), then the rows' sum.
 * Without a field, the rows of the class are summed.
 */
export function classTotal(
  field: number | null | undefined,
  classRows: EchoflicksSessionCostLineItem[],
  rowsLoaded: boolean,
): number | null {
  const stored = finite(field);
  const rowsSum = classRows.length ? sumBillableUsd(classRows) : null;
  if (stored != null) {
    if (!rowsLoaded || Math.abs(stored - (rowsSum ?? 0)) < 0.01) return stored;
    return rowsSum ?? 0;
  }
  return rowsSum;
}

/** Rows of one cost class. */
export function rowsOfClass(
  costs: EchoflicksSessionCostLineItem[],
  costClass: ReturnType<typeof costClassOf>,
): EchoflicksSessionCostLineItem[] {
  return costs.filter((cost) => costClassOf(cost.cost_domain) === costClass);
}
